<template>
  <v-menu
    v-model="datePickerOpen"
    :close-on-content-click="false"
    location="bottom start"
  >
    <template #activator="{ props: datePickerActivatorProps }">
      <AppTextField
        v-bind="datePickerActivatorProps"
        class="picker-control"
        :model-value="formattedDate"
        :label="label"
        :placeholder="placeholder"
        :name="name"
        :width="width"
        :required="required"
        :disabled="disabled"
        :readonly="true"
        :hide-details="hideDetails"
                :density="density"
        append-inner-icon="mdi-calendar-blank-outline"
      />
    </template>

    <v-card
      class="date-field__picker"
      rounded="lg"
    >
      <v-date-picker
        color="primary"
        hide-header
        :model-value="selectedDate"
        @update:model-value="setSelectedDate"
      />
    </v-card>
  </v-menu>
</template>

<script setup>
import { computed, ref } from 'vue'

import AppTextField from './AppTextField.vue'

const props = defineProps({
  modelValue: {
    type: [String, Date],
    default: '',
  },

  label: {
    type: String,
    default: '',
  },

  placeholder: {
    type: String,
    default: '',
  },

  name: {
    type: String,
    default: '',
  },

  width: {
    type: [String, Number],
    default: '100%',
  },

  required: {
    type: Boolean,
    default: false,
  },

  disabled: {
    type: Boolean,
    default: false,
  },

  hideDetails: {
    type: [Boolean, String],
    default: 'auto',
  },

  density: {
    type: String,
    default: 'compact',
    validator: (value) => ['default', 'comfortable', 'compact'].includes(value),
  },
})

const emit = defineEmits(['update:modelValue'])

const datePickerOpen = ref(false)

const selectedDate = computed(() => parseDateValue(props.modelValue))

const formattedDate = computed(() => {
  const date = parseDateValue(props.modelValue)

  if (!date) {
    return ''
  }

  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')

  return `${day}.${month}.${date.getFullYear()}`
})

function setSelectedDate (date) {
  datePickerOpen.value = false

  if (!date) {
    emit('update:modelValue', '')
    return
  }

  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')

  emit('update:modelValue', `${date.getFullYear()}-${month}-${day}`)
}

function parseDateValue (dateValue) {
  if (!dateValue) {
    return null
  }

  if (dateValue instanceof Date) {
    return Number.isNaN(dateValue.getTime()) ? null : dateValue
  }

  const dateParts = dateValue.includes('-')
    ? dateValue.split('-')
    : dateValue.split('.').reverse()

  const [year, month, day] = dateParts.map(Number)
  const parsedDate = new Date(year, month - 1, day)

  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate
}
</script>

<style
  scoped
  lang="scss"
>
.date-field {
  &__picker {
    overflow: hidden;
  }
}
</style>
